import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, MapPin, Phone } from 'lucide-react';
import Layout from '@/components/Layout';
import { getCompanyBySlug, ROUTE_PATHS } from '@/lib/index';

export default function CompanyContact() {
  const { slug } = useParams<{ slug: string }>();
  const company = getCompanyBySlug(slug ?? '');
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  if (!company) return <Layout><div className="min-h-screen flex items-center justify-center"><Link to={ROUTE_PATHS.COMPANIES} className="btn-gold-outline">Back</Link></div></Layout>;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };
  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); setSubmitted(true); };

  return (
    <Layout activeCompanySlug={company.slug}>
      {/* Hero */}
      <section className="relative pt-40 pb-20 min-h-[45vh] flex items-center overflow-hidden bg-[#080808]">
        <div className="absolute inset-0">
          <img src={company.heroImage} alt={company.name} className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-[rgba(8,8,8,0.5)] to-[#080808]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <p className="eyebrow mb-4">{company.name}</p>
            <h1 className="heading-display text-[clamp(3rem,7vw,5rem)] text-[#f5f5f5] max-w-3xl">
              Get in <span className="text-gold italic">Touch</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Contact grid */}
      <section className="py-16 bg-[#080808]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
            {/* Info */}
            <div className="lg:col-span-2 bg-[#0c0c0c] border border-[rgba(201,168,76,0.08)] p-10 lg:p-14">
              <div
                className="w-12 h-12 flex items-center justify-center border text-[11px] font-bold tracking-widest mb-6"
                style={{ borderColor: 'rgba(201,168,76,0.5)', color: '#c9a84c', fontFamily: 'var(--font-sans)' }}
              >
                {company.logoInitials}
              </div>
              <h2 className="heading-display text-2xl text-[#f5f5f5] mb-5">{company.name}</h2>
              <p className="text-[#f5f5f5]/50 text-sm leading-relaxed mb-8">{company.tagline}</p>

              <div className="space-y-5">
                {[
                  { Icon: Mail, label: 'Email', value: company.contactEmail },
                  { Icon: Phone, label: 'Phone', value: '+63 (2) 1234 5678' },
                  { Icon: MapPin, label: 'Location', value: 'BGC, Taguig City\nMetro Manila, Philippines' },
                ].map(({ Icon, label, value }) => (
                  <div key={label} className="flex items-start gap-4">
                    <div className="w-8 h-8 flex items-center justify-center border border-[rgba(201,168,76,0.3)] flex-shrink-0">
                      <Icon size={13} className="text-[#c9a84c]" />
                    </div>
                    <div>
                      <p className="text-[9px] tracking-[0.2em] uppercase text-[#c9a84c]/50 mb-0.5">{label}</p>
                      <p className="text-sm text-[#f5f5f5]/65 whitespace-pre-line">{value}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 pt-8 border-t border-[rgba(201,168,76,0.1)]">
                <Link to={ROUTE_PATHS.HOME} className="inline-flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase text-[#c9a84c]/60 hover:text-[#c9a84c] transition-colors duration-200">
                  ← Back to Main Hub
                </Link>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3 bg-[#090909] border border-[rgba(201,168,76,0.06)] border-l-0 p-10 lg:p-14">
              {submitted ? (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center h-full text-center py-16">
                  <div className="w-14 h-14 border-2 border-[#c9a84c] flex items-center justify-center mb-5">
                    <span className="text-[#c9a84c] text-xl">✓</span>
                  </div>
                  <p className="eyebrow mb-3">Thank You</p>
                  <h3 className="heading-display text-2xl text-[#f5f5f5] mb-3">Message Received</h3>
                  <p className="text-[#f5f5f5]/45 text-sm">We'll be in touch soon.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <p className="eyebrow mb-1">Send a Message</p>
                  <h3 className="heading-display text-2xl text-[#f5f5f5] mb-5">Contact {company.shortName}</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[{ name: 'name', label: 'Full Name', type: 'text', ph: 'Your name' }, { name: 'email', label: 'Email', type: 'email', ph: 'your@email.com' }].map(f => (
                      <div key={f.name}>
                        <label className="block text-[9px] tracking-[0.2em] uppercase text-[#c9a84c]/60 mb-2">{f.label}</label>
                        <input type={f.type} name={f.name} value={form[f.name as keyof typeof form]}
                          onChange={handleChange} placeholder={f.ph} required
                          className="w-full bg-transparent border border-[rgba(201,168,76,0.2)] text-[#f5f5f5] text-sm px-4 py-3.5 placeholder-[rgba(245,245,245,0.2)] focus:outline-none focus:border-[rgba(201,168,76,0.6)] transition-colors duration-300" />
                      </div>
                    ))}
                  </div>

                  <div>
                    <label className="block text-[9px] tracking-[0.2em] uppercase text-[#c9a84c]/60 mb-2">Message</label>
                    <textarea name="message" value={form.message} onChange={handleChange} rows={7}
                      placeholder="Tell us about your inquiry..."
                      required
                      className="w-full bg-transparent border border-[rgba(201,168,76,0.2)] text-[#f5f5f5] text-sm px-4 py-3.5 placeholder-[rgba(245,245,245,0.2)] focus:outline-none focus:border-[rgba(201,168,76,0.6)] transition-colors duration-300 resize-none" />
                  </div>

                  <button type="submit" className="btn-gold-fill">Send Message</button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
